import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import {
  FieldRow,
  NumberField,
  SelectField,
  SettingsCard,
  SwitchField,
} from '@/features/store-settings';

import type { ChannelOverrideController, FieldScope } from '../../model/channelOverrideController';
import { minutesEquivalent } from '../../model/format';
import { formatSettingValue } from '../../model/provenance';
import type {
  ChatbotAfterHoursPolicy,
  ChatbotAiMode,
  ChatbotCapabilityState,
  ChatbotChannel,
  ChatbotDictionaries,
  ChatbotFaqIntent,
  ChatbotSettingsFields,
} from '../../model/types';
import { msToSeconds, outOfRange, secondsToMs } from '../../model/validation';
import { ChannelScopeTabs } from './ChannelScopeTabs';
import type { ChannelScope } from './ChannelScopeTabs';
import { PolicyTopicsField } from './PolicyTopicsField';
import { ResetScopeDialog } from './ResetScopeDialog';
import { ScopedFieldLabel } from './ScopedFieldLabel';
import { ToolAllowlistField } from './ToolAllowlistField';
import { WorkingHoursField } from './WorkingHoursField';

export interface EscalationAiCardProps {
  scope: ChannelScope;
  onScopeChange: (scope: ChannelScope) => void;
  controller: ChannelOverrideController;
  canManage: boolean;
  aiCapability?: ChatbotCapabilityState;
  handoffCapability?: ChatbotCapabilityState;
  canViewTenant: boolean;
  onValidityChange: (valid: boolean) => void;
  dictionaries: ChatbotDictionaries | undefined;
  faqIntents: ChatbotFaqIntent[] | undefined;
  faqLoading: boolean;
  faqError: boolean;
  tenantTimezone?: string;
}

function LockedByPlatformChip() {
  const { t } = useTranslation('chatbot');
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<LockOutlinedIcon fontSize="small" />}
      label={t('locked.chip')}
    />
  );
}

/** Locked-by-platform vs. off-for-tenant helper text (design §9.1). */
function CapabilityHelper({
  capability,
  featureKey,
  canViewTenant,
  fallback,
}: {
  capability: ChatbotCapabilityState | undefined;
  featureKey: 'chatbot' | 'ai' | 'handoff';
  canViewTenant: boolean;
  fallback: ReactNode;
}) {
  const { t } = useTranslation('chatbot');
  if (!capability || capability.effective) {
    return <>{fallback}</>;
  }
  if (capability.lockedByPlatform) {
    return <>{t('locked.platformHelper', { feature: t(`locked.features.${featureKey}`) })}</>;
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      <span>{t('locked.tenantHelper', { feature: t(`locked.features.${featureKey}`) })}</span>
      {canViewTenant ? (
        <Button size="small" component={RouterLink} to="/settings/tenant">
          {t('locked.tenantLink')}
        </Button>
      ) : null}
    </Stack>
  );
}

/** The Escalation & AI card (design §9, extended §17.1/§17.3.2/§17.3.3): AI mode, confidence/loop
 * guards, hand-off, working hours, after-hours, escalation topics, AI timing and the tool allowlist. */
export function EscalationAiCard({
  scope,
  onScopeChange,
  controller,
  canManage,
  aiCapability,
  handoffCapability,
  canViewTenant,
  onValidityChange,
  dictionaries,
  faqIntents,
  faqLoading,
  faqError,
  tenantTimezone,
}: EscalationAiCardProps) {
  const { t } = useTranslation('chatbot');
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [confirmDefaultsOpen, setConfirmDefaultsOpen] = useState(false);
  const [toolsValid, setToolsValid] = useState(true);
  const [workingHoursValid, setWorkingHoursValid] = useState(true);

  const aiMode = controller.field('aiMode');
  const confidenceThreshold = controller.field('confidenceThreshold');
  const lowConfidenceMaxStreak = controller.field('lowConfidenceMaxStreak');
  const loopMaxUnresolvedTurns = controller.field('loopMaxUnresolvedTurns');
  const handoffEnabledField = controller.field('handoffEnabled');
  const escalationQueueTimeoutMinutes = controller.field('escalationQueueTimeoutMinutes');
  const workingHours = controller.field('workingHours');
  const afterHoursPolicy = controller.field('afterHoursPolicy');
  const escalationPolicyTopics = controller.field('escalationPolicyTopics');
  const interimAckAfterMs = controller.field('interimAckAfterMs');
  const agentDeadlineMs = controller.field('agentDeadlineMs');
  const toolAllowlist = controller.field('toolAllowlist');

  const formatCtx = { t, dictionaries };
  function hintFor<K extends keyof ChatbotSettingsFields>(field: FieldScope<K>, key: K): ReactNode {
    if (
      scope !== 'GENERAL' ||
      field.source !== 'GENERAL' ||
      field.platformDefaultValue === undefined
    ) {
      return null;
    }
    const text = field.sameAsDefault
      ? t('provenance.sameAsPlatformDefault')
      : t('provenance.platformDefaultValue', {
          value: formatSettingValue(key, field.platformDefaultValue, formatCtx),
        });
    return (
      <Typography component="span" display="block" variant="caption" color="text.secondary">
        {text}
      </Typography>
    );
  }
  function withHint<K extends keyof ChatbotSettingsFields>(
    base: string,
    field: FieldScope<K>,
    key: K,
  ): ReactNode {
    const hint = hintFor(field, key);
    return hint ? (
      <>
        {base}
        {hint}
      </>
    ) : (
      base
    );
  }
  function scopedLabel<K extends keyof ChatbotSettingsFields>(
    label: string,
    field: FieldScope<K>,
  ): ReactNode {
    return (
      <ScopedFieldLabel
        label={label}
        scope={scope}
        source={field.source}
        pending={field.pending}
        onUseGeneral={field.onUseGeneral}
        onCustomiseRow={field.onCustomiseRow}
        onUseDefault={field.onUseDefault}
        onKeepYours={field.onKeepYours}
      />
    );
  }

  const aiModeLabel = t('escalation.aiMode.label');
  const confidenceLabel = t('escalation.confidence.label');
  const streakLabel = t('escalation.streak.label');
  const loopLabel = t('escalation.loop.label');
  const handoffLabel = t('escalation.handoff.label');
  const queueTimeoutLabel = t('escalation.queueTimeout.label');
  const afterHoursLabel = t('escalation.afterHours.label');
  const policyTopicsLabel = t('escalation.policyTopics.label');
  const interimAckLabel = t('escalation.interimAck.label');
  const deadlineLabel = t('escalation.deadline.label');
  const toolsLabel = t('escalation.tools.label');

  const aiLocked = aiCapability?.effective === false && aiCapability.lockedByPlatform === true;
  const handoffLocked =
    handoffCapability?.effective === false && handoffCapability.lockedByPlatform === true;
  const handoffDimmed = !handoffEnabledField.value;

  const confidenceError = outOfRange(confidenceThreshold.value, 0, 1)
    ? t('validation.range', { min: 0, max: 1 })
    : null;
  const streakError = outOfRange(lowConfidenceMaxStreak.value, 1, 10)
    ? t('validation.range', { min: 1, max: 10 })
    : null;
  const loopError = outOfRange(loopMaxUnresolvedTurns.value, 1, 10)
    ? t('validation.range', { min: 1, max: 10 })
    : null;
  const queueTimeoutError = outOfRange(escalationQueueTimeoutMinutes.value, 5, 1440)
    ? t('validation.range', { min: 5, max: 1440 })
    : null;
  const interimAckSeconds = msToSeconds(interimAckAfterMs.value ?? 0);
  const agentDeadlineSeconds = msToSeconds(agentDeadlineMs.value ?? 0);
  const interimAckError = outOfRange(interimAckSeconds, 1, 30)
    ? t('validation.range', { min: 1, max: 30 })
    : null;
  const agentDeadlineRangeError = outOfRange(agentDeadlineSeconds, 2, 60)
    ? t('validation.range', { min: 2, max: 60 })
    : null;
  const agentDeadlineCrossError =
    !agentDeadlineRangeError &&
    agentDeadlineMs.value !== null &&
    interimAckAfterMs.value !== null &&
    agentDeadlineMs.value < interimAckAfterMs.value
      ? t('escalation.deadline.mustExceedInterim')
      : null;
  const agentDeadlineError = agentDeadlineRangeError ?? agentDeadlineCrossError;

  useEffect(() => {
    onValidityChange(
      !confidenceError &&
        !streakError &&
        !loopError &&
        !queueTimeoutError &&
        !interimAckError &&
        !agentDeadlineError &&
        toolsValid &&
        workingHoursValid,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    confidenceError,
    streakError,
    loopError,
    queueTimeoutError,
    interimAckError,
    agentDeadlineError,
    toolsValid,
    workingHoursValid,
  ]);

  const overridesByChannel: Record<ChatbotChannel, boolean> = {
    TELEGRAM: controller.hasOverride('TELEGRAM'),
    MAX: controller.hasOverride('MAX'),
  };

  const activeChannel: ChatbotChannel | null = scope === 'GENERAL' ? null : scope;
  const handleReset = () => {
    if (controller.activeChannelCardDirty) setConfirmResetOpen(true);
    else controller.resetCard();
  };

  const queueTimeoutEquivalent = minutesEquivalent(escalationQueueTimeoutMinutes.value ?? 0);
  const resettableCount = controller.resettableGeneralFields.length;

  return (
    <SettingsCard
      title={t('escalation.title')}
      description={t('escalation.description')}
      icon={<SupportAgentRoundedIcon />}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {scope === 'GENERAL' && canManage && resettableCount > 0 ? (
            <Button size="small" onClick={() => setConfirmDefaultsOpen(true)}>
              {t('provenance.resetCard')}
            </Button>
          ) : null}
          <ChannelScopeTabs
            value={scope}
            onChange={onScopeChange}
            overrides={overridesByChannel}
            cardTitle={t('escalation.title')}
          />
        </Stack>
      }
    >
      {activeChannel ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          {controller.activeChannelHasOverride ? (
            <Button size="small" onClick={handleReset} disabled={!canManage}>
              {t('scope.resetToGeneral')}
            </Button>
          ) : (
            <Button size="small" onClick={controller.customiseCard} disabled={!canManage}>
              {t('scope.customiseFor', { channel: t(`scope.${activeChannel.toLowerCase()}`) })}
            </Button>
          )}
        </Stack>
      ) : null}

      <FieldRow
        label={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {scopedLabel(aiModeLabel, aiMode)}
            {aiLocked ? <LockedByPlatformChip /> : null}
          </Stack>
        }
        description={
          <CapabilityHelper
            capability={aiCapability}
            featureKey="ai"
            canViewTenant={canViewTenant}
            fallback={withHint(t('escalation.aiMode.description'), aiMode, 'aiMode')}
          />
        }
      >
        <SelectField
          label={aiModeLabel}
          value={aiMode.value}
          options={[
            { value: 'OFF', label: t('escalation.aiMode.options.off') },
            { value: 'CLASSIFY_ONLY', label: t('escalation.aiMode.options.classifyOnly') },
            { value: 'ASSIST', label: t('escalation.aiMode.options.assist') },
          ]}
          onChange={(value) => aiMode.onChange(value as ChatbotAiMode)}
          disabled={aiMode.disabled || aiLocked}
        />
      </FieldRow>

      <NumberField
        label={scopedLabel(confidenceLabel, confidenceThreshold)}
        ariaLabel={confidenceLabel}
        description={withHint(
          t('escalation.confidence.description'),
          confidenceThreshold,
          'confidenceThreshold',
        )}
        value={confidenceThreshold.value}
        onChange={(value) => confidenceThreshold.onChange(value ?? 0.6)}
        disabled={confidenceThreshold.disabled || aiLocked}
        min={0}
        max={1}
        step={0.05}
        error={Boolean(confidenceError)}
        helperText={confidenceError}
      />
      <NumberField
        label={scopedLabel(streakLabel, lowConfidenceMaxStreak)}
        ariaLabel={streakLabel}
        description={withHint(
          t('escalation.streak.description'),
          lowConfidenceMaxStreak,
          'lowConfidenceMaxStreak',
        )}
        value={lowConfidenceMaxStreak.value}
        onChange={(value) => lowConfidenceMaxStreak.onChange(value ?? 2)}
        disabled={lowConfidenceMaxStreak.disabled || aiLocked}
        min={1}
        max={10}
        error={Boolean(streakError)}
        helperText={streakError}
      />
      <NumberField
        label={scopedLabel(loopLabel, loopMaxUnresolvedTurns)}
        ariaLabel={loopLabel}
        description={withHint(
          t('escalation.loop.description'),
          loopMaxUnresolvedTurns,
          'loopMaxUnresolvedTurns',
        )}
        value={loopMaxUnresolvedTurns.value}
        onChange={(value) => loopMaxUnresolvedTurns.onChange(value ?? 3)}
        disabled={loopMaxUnresolvedTurns.disabled}
        min={1}
        max={10}
        error={Boolean(loopError)}
        helperText={loopError}
      />

      <FieldRow
        label={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {scopedLabel(handoffLabel, handoffEnabledField)}
            {handoffLocked ? <LockedByPlatformChip /> : null}
          </Stack>
        }
        description={
          <CapabilityHelper
            capability={handoffCapability}
            featureKey="handoff"
            canViewTenant={canViewTenant}
            fallback={withHint(
              t('escalation.handoff.description'),
              handoffEnabledField,
              'handoffEnabled',
            )}
          />
        }
      >
        <SwitchField
          label={handoffLabel}
          checked={handoffEnabledField.value}
          onChange={(value) => handoffEnabledField.onChange(value)}
          disabled={handoffEnabledField.disabled || handoffLocked}
        />
      </FieldRow>

      <NumberField
        label={scopedLabel(queueTimeoutLabel, escalationQueueTimeoutMinutes)}
        ariaLabel={queueTimeoutLabel}
        description={
          handoffDimmed
            ? t('escalation.handoff.appliesWhenOn')
            : withHint(
                t('escalation.queueTimeout.description'),
                escalationQueueTimeoutMinutes,
                'escalationQueueTimeoutMinutes',
              )
        }
        value={escalationQueueTimeoutMinutes.value}
        onChange={(value) => escalationQueueTimeoutMinutes.onChange(value ?? 30)}
        disabled={escalationQueueTimeoutMinutes.disabled}
        min={5}
        max={1440}
        suffix={
          queueTimeoutEquivalent && queueTimeoutEquivalent.unit !== 'minutes'
            ? `${t('units.minutes')} ${t('units.equals', { value: `${queueTimeoutEquivalent.value} ${t(`units.${queueTimeoutEquivalent.unit}`)}` })}`
            : t('units.minutes')
        }
        error={Boolean(queueTimeoutError)}
        helperText={queueTimeoutError ?? t('escalation.queueTimeout.helper')}
      />

      <WorkingHoursField
        // Remounts on scope switch — its `timezoneTouched` state must not leak from one scope's
        // working-hours value to another's (same root cause as `ToolAllowlistField`'s `key` below).
        key={scope}
        value={workingHours.value}
        onChange={(value) => workingHours.onChange(value)}
        disabled={workingHours.disabled}
        canManage={canManage}
        tenantTimezone={tenantTimezone}
        labelAdornment={scopedLabel(t('escalation.workingHours.label'), workingHours)}
        onValidityChange={setWorkingHoursValid}
      />

      <SelectField
        label={scopedLabel(afterHoursLabel, afterHoursPolicy)}
        ariaLabel={afterHoursLabel}
        description={withHint(
          t('escalation.afterHours.description'),
          afterHoursPolicy,
          'afterHoursPolicy',
        )}
        value={afterHoursPolicy.value}
        options={[
          { value: 'BOT_ONLY', label: t('escalation.afterHours.options.botOnly') },
          { value: 'QUEUE', label: t('escalation.afterHours.options.queue') },
          { value: 'TEMPLATE', label: t('escalation.afterHours.options.template') },
        ]}
        onChange={(value) => afterHoursPolicy.onChange(value as ChatbotAfterHoursPolicy)}
        disabled={afterHoursPolicy.disabled}
      />

      <PolicyTopicsField
        label={scopedLabel(policyTopicsLabel, escalationPolicyTopics)}
        description={withHint(
          t('escalation.policyTopics.description'),
          escalationPolicyTopics,
          'escalationPolicyTopics',
        )}
        ariaLabel={policyTopicsLabel}
        value={escalationPolicyTopics.value ?? []}
        onChange={escalationPolicyTopics.onChange}
        disabled={escalationPolicyTopics.disabled}
        intents={dictionaries?.intents}
        faqIntents={faqIntents}
        faqLoading={faqLoading}
        faqError={faqError}
      />

      <NumberField
        label={scopedLabel(interimAckLabel, interimAckAfterMs)}
        ariaLabel={interimAckLabel}
        description={withHint(
          t('escalation.interimAck.description'),
          interimAckAfterMs,
          'interimAckAfterMs',
        )}
        value={interimAckSeconds}
        onChange={(value) => interimAckAfterMs.onChange(secondsToMs(value ?? 3))}
        disabled={interimAckAfterMs.disabled}
        min={1}
        max={30}
        suffix={t('escalation.interimAck.suffix')}
        error={Boolean(interimAckError)}
        helperText={interimAckError}
      />
      <NumberField
        label={scopedLabel(deadlineLabel, agentDeadlineMs)}
        ariaLabel={deadlineLabel}
        description={withHint(
          t('escalation.deadline.description'),
          agentDeadlineMs,
          'agentDeadlineMs',
        )}
        value={agentDeadlineSeconds}
        onChange={(value) => agentDeadlineMs.onChange(secondsToMs(value ?? 15))}
        disabled={agentDeadlineMs.disabled}
        min={2}
        max={60}
        suffix={t('escalation.deadline.suffix')}
        error={Boolean(agentDeadlineError)}
        helperText={agentDeadlineError}
      />

      <ToolAllowlistField
        // Remounts on scope switch — its local `uiMode` (ALL/ONLY) is seeded once from `value` and
        // only ever self-corrects towards ONLY, so reusing the same instance across General/
        // Telegram/MAX left a stale ONLY-with-nothing-ticked state (and a page-wide disabled Save)
        // after switching to a scope whose own `toolAllowlist` starts empty.
        key={scope}
        label={scopedLabel(toolsLabel, toolAllowlist)}
        description={withHint(t('escalation.tools.description'), toolAllowlist, 'toolAllowlist')}
        ariaLabel={toolsLabel}
        value={toolAllowlist.value ?? []}
        onChange={toolAllowlist.onChange}
        disabled={toolAllowlist.disabled}
        tools={dictionaries?.tools}
        dimmed={aiMode.value !== 'ASSIST'}
        onValidityChange={setToolsValid}
      />

      {activeChannel ? (
        <ResetScopeDialog
          open={confirmResetOpen}
          channel={activeChannel}
          onConfirm={() => {
            controller.resetCard();
            setConfirmResetOpen(false);
          }}
          onCancel={() => setConfirmResetOpen(false)}
        />
      ) : null}

      <Dialog open={confirmDefaultsOpen} onClose={() => setConfirmDefaultsOpen(false)}>
        <DialogTitle>
          {t('provenance.resetCardDialog.title', { card: t('escalation.title') })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              resettableCount === 1
                ? 'provenance.resetCardDialog.body_one'
                : 'provenance.resetCardDialog.body_other',
              { count: resettableCount },
            )}
            {aiLocked || handoffLocked ? ` ${t('provenance.resetCardDialog.lockedKept')}` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDefaultsOpen(false)} autoFocus>
            {t('common:action.cancel')}
          </Button>
          <Button
            onClick={() => {
              controller.useCardDefaults();
              setConfirmDefaultsOpen(false);
            }}
          >
            {t('provenance.resetCardDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsCard>
  );
}
